import { useState } from "react";
import type { SubmissionFilter } from "@sga/shared";
import type { ColumnDefinition, SavedView } from "./useSubmissions.js";

/**
 * The filter bar and saved-view manager — docs/01-PRODUCT-SPEC.md §9.1.
 *
 * Filters are built from the column list the server returned, so a form's own fields are
 * filterable without the dashboard knowing anything about them in advance. The operator list is
 * narrowed by field type: "is one of" on a free-text field is a trap, and a range on a text field
 * is meaningless.
 */

const OPERATORS_BY_TYPE: Record<string, SubmissionFilter["op"][]> = {
  number: ["numberBetween", "eq", "isEmpty", "isNotEmpty"],
  date: ["dateBetween", "isEmpty", "isNotEmpty"],
  radio: ["in", "eq", "isEmpty", "isNotEmpty"],
  select: ["in", "eq", "isEmpty", "isNotEmpty"],
  checkbox: ["in", "eq", "isEmpty", "isNotEmpty"],
  state: ["in", "eq", "isEmpty", "isNotEmpty"],
  lga: ["in", "eq", "isEmpty", "isNotEmpty"],
};

const DEFAULT_OPERATORS: SubmissionFilter["op"][] = ["contains", "eq", "isEmpty", "isNotEmpty"];

const OPERATOR_LABELS: Record<SubmissionFilter["op"], string> = {
  contains: "contains",
  eq: "is",
  in: "is one of",
  numberBetween: "is between",
  dateBetween: "is between dates",
  isEmpty: "is empty",
  isNotEmpty: "is not empty",
};

const META_COLUMNS: ColumnDefinition[] = [
  {
    key: "reviewStatus",
    label: "Review status",
    type: "radio",
    sortKind: "text",
    isPii: false,
    isSensitive: false,
    masked: false,
  },
  {
    key: "status",
    label: "Submission status",
    type: "radio",
    sortKind: "text",
    isPii: false,
    isSensitive: false,
    masked: false,
  },
  {
    key: "submittedAt",
    label: "Date submitted",
    type: "date",
    sortKind: "temporal",
    isPii: false,
    isSensitive: false,
    masked: false,
  },
  // The person rather than an answer. Both were filterable by the API all along and offered by
  // nothing — the only way to narrow to one applicant was the free-text search across every field.
  {
    key: "respondentName",
    label: "Applicant name",
    type: "short_text",
    sortKind: "text",
    isPii: true,
    isSensitive: false,
    masked: false,
  },
  {
    key: "respondentEmail",
    label: "Applicant email",
    type: "short_text",
    sortKind: "text",
    isPii: true,
    isSensitive: false,
    masked: false,
  },
  {
    key: "source",
    label: "Source",
    type: "radio",
    sortKind: "text",
    isPii: false,
    isSensitive: false,
    masked: false,
  },
  {
    key: "utm.source",
    label: "UTM source",
    type: "short_text",
    sortKind: "text",
    isPii: false,
    isSensitive: false,
    masked: false,
  },
  {
    key: "duplicateFlag",
    label: "Flagged duplicate",
    type: "radio",
    sortKind: "text",
    isPii: false,
    isSensitive: false,
    masked: false,
  },
];

interface Props {
  columns: ColumnDefinition[];
  filters: SubmissionFilter[];
  onFiltersChange: (filters: SubmissionFilter[]) => void;
  views: SavedView[];
  activeViewId?: string;
  onViewSelect: (viewId: string | undefined) => void;
  onSaveView: (name: string, isShared: boolean, isDefault: boolean) => void;
  onDeleteView: (viewId: string) => void;
  search: string;
  onSearchChange: (value: string) => void;
}

export const FilterBar = ({
  columns,
  filters,
  onFiltersChange,
  views,
  activeViewId,
  onViewSelect,
  onSaveView,
  onDeleteView,
  search,
  onSearchChange,
}: Props): JSX.Element => {
  const [saving, setSaving] = useState(false);
  const [viewName, setViewName] = useState("");
  const [shared, setShared] = useState(false);
  const [isDefault, setIsDefault] = useState(false);

  // An encrypted field is not filterable and must not appear to be. Its value is not in `data` at
  // all, so every comparison would match nothing — and a filter that silently matches nothing is
  // also a way to probe for a value by watching which guesses return rows.
  const allColumns = [...META_COLUMNS, ...columns.filter((column) => !column.isSensitive)];
  const columnFor = (field: string): ColumnDefinition | undefined =>
    allColumns.find((column) => column.key === field);

  const addFilter = (): void => {
    const first = allColumns[0];
    if (!first) return;
    onFiltersChange([...filters, { field: first.key, op: "contains", value: "" }]);
  };

  const updateFilter = (index: number, next: SubmissionFilter): void => {
    onFiltersChange(filters.map((filter, i) => (i === index ? next : filter)));
  };

  const removeFilter = (index: number): void => {
    onFiltersChange(filters.filter((_, i) => i !== index));
  };

  const operatorsFor = (field: string): SubmissionFilter["op"][] => {
    const column = columnFor(field);
    return (column && OPERATORS_BY_TYPE[column.type]) ?? DEFAULT_OPERATORS;
  };

  const blankFilterFor = (field: string, op: SubmissionFilter["op"]): SubmissionFilter => {
    switch (op) {
      case "in":
        return { field, op, values: [] };
      case "numberBetween":
        return { field, op };
      case "dateBetween":
        return { field, op };
      case "isEmpty":
      case "isNotEmpty":
        return { field, op };
      case "eq":
        return { field, op, value: "" };
      case "contains":
        return { field, op, value: "" };
    }
  };

  return (
    <div className="flex flex-col gap-3 border-b pb-3">
      <div className="flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor="submissions-search">
          Search within this form
        </label>
        {/* Wide and first, because "is this person in here" is the question the table is asked
            most often, and the answer used to be an export. Searches name, email and reference as
            well as every searchable answer — never an encrypted field, which is not in the index. */}
        <input
          id="submissions-search"
          type="search"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search name, email, reference or any answer…"
          className="w-80 max-w-full rounded-md border px-3 py-1.5 text-sm"
        />

        <select
          aria-label="Saved view"
          value={activeViewId ?? ""}
          onChange={(event) => onViewSelect(event.target.value || undefined)}
          className="rounded-md border px-2 py-1.5 text-sm"
        >
          <option value="">All submissions</option>
          {views.map((view) => (
            <option key={view.id} value={view.id}>
              {view.name}
              {view.isShared && !view.isMine ? ` (${view.ownerName})` : ""}
              {view.isDefault ? " ★" : ""}
            </option>
          ))}
        </select>

        {activeViewId && views.find((view) => view.id === activeViewId)?.isMine && (
          <button
            type="button"
            className="text-sm text-brand-red underline"
            onClick={() => onDeleteView(activeViewId)}
          >
            Delete view
          </button>
        )}

        <button type="button" onClick={addFilter} className="rounded-md border px-3 py-1.5 text-sm">
          Add filter
        </button>

        <button
          type="button"
          onClick={() => setSaving((open) => !open)}
          className="rounded-md border px-3 py-1.5 text-sm"
        >
          Save as view
        </button>
      </div>

      {saving && (
        <form
          className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 p-2 text-sm"
          onSubmit={(event) => {
            event.preventDefault();
            if (!viewName.trim()) return;
            onSaveView(viewName.trim(), shared, isDefault);
            setViewName("");
            setSaving(false);
          }}
        >
          <input
            aria-label="View name"
            value={viewName}
            onChange={(event) => setViewName(event.target.value)}
            placeholder="Delta shortlist candidates"
            className="w-64 rounded-md border px-2 py-1"
          />
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
            Share with my programme team
          </label>
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            Make this my default
          </label>
          <button type="submit" className="rounded-md bg-primary px-3 py-1 text-primary-foreground">
            Save
          </button>
        </form>
      )}

      {filters.map((filter, index) => {
        const operators = operatorsFor(filter.field);
        return (
          <div key={index} className="flex flex-wrap items-center gap-2 text-sm">
            <select
              aria-label="Filter field"
              value={filter.field}
              onChange={(event) =>
                updateFilter(
                  index,
                  blankFilterFor(
                    event.target.value,
                    operatorsFor(event.target.value)[0] ?? "contains",
                  ),
                )
              }
              className="rounded-md border px-2 py-1"
            >
              {allColumns.map((column) => (
                <option key={column.key} value={column.key}>
                  {column.label}
                </option>
              ))}
            </select>

            <select
              aria-label="Filter operator"
              value={filter.op}
              onChange={(event) =>
                updateFilter(
                  index,
                  blankFilterFor(filter.field, event.target.value as SubmissionFilter["op"]),
                )
              }
              className="rounded-md border px-2 py-1"
            >
              {operators.map((operator) => (
                <option key={operator} value={operator}>
                  {OPERATOR_LABELS[operator]}
                </option>
              ))}
            </select>

            {(filter.op === "contains" || filter.op === "eq") && (
              <input
                aria-label="Filter value"
                value={String(filter.value ?? "")}
                onChange={(event) => updateFilter(index, { ...filter, value: event.target.value })}
                className="rounded-md border px-2 py-1"
              />
            )}

            {filter.op === "in" && (
              <input
                aria-label="Filter values, comma separated"
                value={filter.values.join(", ")}
                onChange={(event) =>
                  updateFilter(index, {
                    ...filter,
                    values: event.target.value
                      .split(",")
                      .map((part) => part.trim())
                      .filter(Boolean),
                  })
                }
                placeholder="Delta, Edo"
                className="w-64 rounded-md border px-2 py-1"
              />
            )}

            {filter.op === "numberBetween" && (
              <>
                <input
                  type="number"
                  aria-label="Minimum"
                  value={filter.min ?? ""}
                  onChange={(event) =>
                    updateFilter(index, {
                      ...filter,
                      min: event.target.value === "" ? undefined : Number(event.target.value),
                    })
                  }
                  className="w-24 rounded-md border px-2 py-1"
                />
                <span>and</span>
                <input
                  type="number"
                  aria-label="Maximum"
                  value={filter.max ?? ""}
                  onChange={(event) =>
                    updateFilter(index, {
                      ...filter,
                      max: event.target.value === "" ? undefined : Number(event.target.value),
                    })
                  }
                  className="w-24 rounded-md border px-2 py-1"
                />
              </>
            )}

            {filter.op === "dateBetween" && (
              <>
                <input
                  type="date"
                  aria-label="From date"
                  value={filter.from?.slice(0, 10) ?? ""}
                  onChange={(event) =>
                    updateFilter(index, {
                      ...filter,
                      from: event.target.value
                        ? new Date(event.target.value).toISOString()
                        : undefined,
                    })
                  }
                  className="rounded-md border px-2 py-1"
                />
                <span>and</span>
                <input
                  type="date"
                  aria-label="To date"
                  value={filter.to?.slice(0, 10) ?? ""}
                  onChange={(event) =>
                    updateFilter(index, {
                      ...filter,
                      to: event.target.value
                        ? new Date(event.target.value).toISOString()
                        : undefined,
                    })
                  }
                  className="rounded-md border px-2 py-1"
                />
              </>
            )}

            <button
              type="button"
              onClick={() => removeFilter(index)}
              aria-label="Remove filter"
              className="rounded-md border px-2 py-1"
            >
              ✕
            </button>
          </div>
        );
      })}
    </div>
  );
};
